import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { 
  Product2, Company, RoomType, ThemeLineItemType, ScopeVariant, SituViewWebApiClient, CreateRequestedSupplierStateRequest, 
  CreateRequestedSupplierResponse, ScoredThemeReplacementTag
} from 'situview-webapi-client';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ConfigService } from 'src/app/services/config.service';
import "@google/model-viewer/dist/model-viewer";
import { CompanyViewService, ProductService, ProductTypeWithColor, SearchThemeReplacementTagsOptions, ThemeService } from 'src/app/services';
import { Observable, Subscription, Subject } from 'rxjs';
import { FormControl } from '@angular/forms';
import { map, startWith, debounceTime } from 'rxjs/operators';

@Component({
  selector: 'product-selector-dialog',
  templateUrl: './product-selector-dialog.component.html',
  styleUrls: ['./product-selector-dialog.component.scss']
})
export class ProductSelectorDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  /* Product Drawer */
  isDrawerOpen = false;
  productConfirmed: boolean | undefined;
  selectedType: ThemeLineItemType | undefined;
  selectedScope: RoomType = RoomType.NotSet;
  selectedScopeVariant: ScopeVariant = ScopeVariant.Default;
  url: string | undefined;
  posterUrl: string | undefined;
  selectedTags: string[] = [];

  selectedProducts: Product2[] = [];

  /* Search */
  searchTerm = "";

  /* Request Drawer */
  isRequestDrawerOpen = false;
  requestedProductType: ProductTypeWithColor | undefined;
  requestedCompany: Company | undefined;
  requestingSupplier: boolean = false;
  requestedSupplier: string | undefined;
  requestedSupplierId: string | undefined;

  /* UI Data */
  allProductTypes: ProductTypeWithColor[] = [];
  themeLineItemTypes: ThemeLineItemType[] = [];
  tags: ScoredThemeReplacementTag[] = [];
  filteredTags: string[] = [];
  remainingFilteredTags: string[] = [];
  showNonRecomendedTags: boolean = false;
  roomTypes = RoomType;
  productWidth: number = 150;

  @ViewChild('modelContainer') modelContainer: ElementRef | undefined;
  _subscriptions: Subscription[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ProductSelectorDialogData,
    private dialogRef: MatDialogRef<ProductSelectorDialogData>,
    private snackBar: MatSnackBar,
    private configService: ConfigService,
    private companyViewService: CompanyViewService,
    private productService: ProductService,
    private themeService: ThemeService,
    private cdr: ChangeDetectorRef,
    private situViewClient: SituViewWebApiClient) {
      if (!data.tagSelectionEnabled) data.tagSelectionEnabled = false;

      this._subscriptions.push(this.filterChange.pipe( debounceTime(300) )
      .subscribe(() => {
        this.filterTags();
      }))
    }

  ngOnInit(): void {
    Promise.all([this.companyControlInit(), this.loadThemeLineItemTypes(), this.loadProductTypes()]);
  }

  ngAfterViewInit() {
    this.resizeModelViewer();
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  requestSupplier() {
    if (this.supplierControl.value) {
      let request = new CreateRequestedSupplierStateRequest();
      request.name = this.supplierControl.value;

      this.situViewClient.createRequestedSupplier(request)
        .then((response: CreateRequestedSupplierResponse) => {
          if (response.success) {
            this.requestedSupplier = response.item?.name;
            this.requestedSupplierId = response.item?.id;
          } else {
            this.showMessage(response?.errorMessages![0]);
          }
        })
        .catch((err: any) => {
          this.showMessage(err);
        });
    }
  }

  getProductTypeColor(productTypeId: string) {
    return this.allProductTypes.find(x => x.id === productTypeId)?.color; 
  }

  getProductTypeName(productTypeId: string) {
    return this.allProductTypes.find(x => x.id === productTypeId)?.name;
  }
  
  getCompanyLogo(companyId: string) {
    return this.allCompanies.find(x => x.id === companyId)?.logoUrl;
  }

  getCompanyName(companyId: string) {
    return this.allCompanies.find(x => x.id === companyId)?.name;
  }

  onProductDetailsClose() {
    this.productConfirmed = undefined;
    this.selectedType = undefined;
    this.selectedScope = RoomType.NotSet;
    this.selectedScopeVariant = ScopeVariant.Default;
    this.isDrawerOpen = false;
    this.searchTerm = "";
    this.selectedTags = [];
    this.tags = [];
    this.filterTags();
  }

  onRequestClose() {
    this.requestedProductType = undefined;
    this.requestedCompany = undefined;
    this.isRequestDrawerOpen = false;
    this.requestingSupplier = false;
    this.requestedSupplier = undefined;
    this.requestedSupplierId = undefined;
    this.companyControl.reset();
  }

  onProductSelected(products: Product2[]) {
    this.selectedProducts = products;

    this.isDrawerOpen = true;
    
    var product = this.selectedProducts[0];
    if (product?.previewArtifactId) {
      this.url = `${this.configService.config.situViewWebApiBaseUrl}/home/getglb?artifactid=${product.previewArtifactId}&filename=product-web.glb`;
      this.posterUrl = `${this.configService.config.situViewWebApiBaseUrl}/home/getimage?artifactid=${product.previewArtifactId}&filename=preview.webp`;
    } else {
      this.url = undefined;
      this.posterUrl = undefined;
    }
  }

  selectProduct() {
    if (this.selectedProducts.length > 0) {

      this.productConfirmed = true;

      let productType = this.allProductTypes.find(x => x.id === this.selectedProducts[0]?.productTypeId);
      if (productType) {
        this.selectedType = this.getThemeLineItemType(productType?.templateName, productType.name);

        // Skip the tag selection if it has not been asked for
        if (!this.data.tagSelectionEnabled){ 
          this.dialogRef.close(this.getReturnData());
          return;
        }

        if (this.selectedType) {
          this.loadTagOptions(this.selectedType)
            .then((tags: ScoredThemeReplacementTag[]) => {
                this.tags = tags ?? [];
                if (this.tags?.length === 1) {
                  this.selectTag(this.tags[0].tag!);
                  this.dialogRef.close(this.getReturnData());
                } else {
                  this.filterTags();
                }
            });
        }
      }
    } else {
      this.showMessage('No product selected');
    }
  }

  selectType(themeLineItemType: ThemeLineItemType) {
    if (themeLineItemType) {
      this.selectedType = themeLineItemType;
      this.loadTagOptions(this.selectedType)
        .then((tags: ScoredThemeReplacementTag[]) => {
            this.tags = tags ?? [];
            if (this.tags?.length === 1) {
              this.selectTag(this.tags[0].tag!);
              this.dialogRef.close(this.getReturnData());
            } else {
              this.filterTags();
            }
        });
    }
  }

  loadTagOptions(themeLineItemType?: ThemeLineItemType): Promise<ScoredThemeReplacementTag[]> {
    if (this.data?.companyId) {

      var options = new SearchThemeReplacementTagsOptions();
      options.themeLineItemTypeId = themeLineItemType?.id;
      options.tagGroupFilter = themeLineItemType?.themeReplacementTagGroup ? [themeLineItemType.themeReplacementTagGroup] : undefined;
      options.productTypeIds = this.selectedProducts[0]?.productTypeId ? [this.selectedProducts[0].productTypeId] : undefined;
      options.productSpecificationIds = this.selectedProducts[0]?.productSpecificationIds;
      
      return this.themeService.SearchThemeReplacementTags(this.data.companyId, options)
        .catch((err: any) => {
          return [];
        });
    } else {
      return Promise.resolve([]);
    }
  }

  private filterChange = new Subject();
  emitChange(): void {
    this.filterChange.next();
  }

  private filterTags() {
    var hasHighScore = this.tags.some(x => x.score! > 1);
    var filtered = this.tags
      .filter(x => !this.selectedTags.includes(x.tag!));

    if (this.searchTerm) {
      var filterValue = this.searchTerm.toLowerCase();
      filtered = filtered
        .filter(x => x.tag!.toLowerCase().includes(filterValue));
    }

    var highScore: string[] = [];
    var lowScore: string[] = [];

    filtered.forEach(x => {
      if (x.score! > 1) {
        highScore.push(x.tag!);
      } else {
        lowScore.push(x.tag!);
      }
    });

    if (hasHighScore) {
      this.filteredTags = highScore;
      this.remainingFilteredTags = lowScore;
    } else {
      this.filteredTags = lowScore;
      this.remainingFilteredTags = [];
    }
  }

  selectTag(tag: string) {
    this.selectedTags.push(tag);
    this.filterTags();
  }
  
  removeTag(tag: string) {
    this.selectedTags = this.selectedTags.filter(x => x.toLowerCase() !== tag.toLowerCase());
    this.filterTags();
  }

  confirmTags() {
    this.dialogRef.close(this.getReturnData());
  }  

  selectScope(scope: RoomType) {
    if (scope) {
      this.selectedScope = scope;
      this.dialogRef.close(this.getReturnData());
    }
  }

  getEnumKeyForDisplay(key: string) {
    if (key == null) {
      return '';
    }

    let display = key.replace(/([A-Z])/g, ' $1').trim();
    let words = display.split(' ');
    display = '';
    for (let i = 0; i < words.length; i++) {
      const word = words[i];
      const nextWord = words[i + 1];
      display += word;
      if (word.length > 1 || nextWord?.length > 1) {
        display += ' ';
      }
    }

    display = display.trim();
    return display;
  }

  /*  Company AutoComplete Filter */
  companyControl = new FormControl();
  supplierControl = new FormControl();
  filteredCompanies: Observable<Company[]> | undefined;
  allCompanies: Company[] = [];
  onCompanySelected(event: any): void {
    this.requestedCompany = event.option.value;
  }

  clearCompany() {
    this.companyControl.reset();
    this.requestedCompany = undefined;
    this.requestedSupplier = undefined;
    this.requestedSupplierId = undefined
  }

  private companyControlInit() {
    this.companyViewService.LoadCompanies()
      .then((companies: Company[]) => {
        this.allCompanies = companies;
        this.filteredCompanies = this.companyControl.valueChanges.pipe(
          startWith(''),
          map(value => typeof value === 'string' ? this._filterCompanies(value) : this.allCompanies)
        );
      });
  }

  private _filterCompanies(value: string): Company[] {
    if (!value) return this.allCompanies; 
    const filterValue = value.toLowerCase() ?? "";
    return this.allCompanies.filter(x => x.name).filter(theme => theme.name!.toLowerCase().includes(filterValue));
  }
  /*  Company AutoComplete Filter */

  private getThemeLineItemType(templateName: string | undefined, productName?: string): ThemeLineItemType | undefined {
    const matchingTypes = this.themeLineItemTypes.filter(item =>
      item.properties?.some(property => property.templateName?.toLowerCase() === templateName?.toLowerCase())
    );
  
    if (matchingTypes.length === 1) {
      return matchingTypes[0];
    } else if (matchingTypes.length > 1 && productName) {
      return matchingTypes.find(item => item.name?.toLowerCase() === productName?.toLowerCase());
    }
  
    return undefined;
  }

  private getReturnData(): ProductSelectorResultData {
    return { 
      products: this.selectedProducts, 
      type: this.selectedType, 
      tags: this.selectedTags, 
      scope: this.selectedScope, 
      scopeVariant: this.selectedScopeVariant,
      propertyName: this.data?.propertyName,
      groupKey: this.data?.groupKey,
      tagSelectionEnabled: this.data?.tagSelectionEnabled
    } as ProductSelectorResultData;
  }

  private loadCompanies() {
    return this.companyViewService.LoadCompanies()
      .then((companies: Company[]) => {
        this.allCompanies = companies;
      });
  }

  private loadProductTypes() {
    return this.productService.LoadProductTypesWithColor()
      .then((productTypes: ProductTypeWithColor[]) => {
        this.allProductTypes = productTypes;
      });
  }

  private resizeModelViewer() {
    if (this.modelContainer) {
      const parentWidth = this.modelContainer.nativeElement.offsetWidth;
  
      // Set the model-viewer size
      this.productWidth = parentWidth * 0.9;
      this.cdr.detectChanges(); // Trigger change detection manually
    }
  }

  private loadThemeLineItemTypes() {
    return this.themeService.LoadThemeLineItemTypes()
      .then((themeLineItemTypes: ThemeLineItemType[]) => {
        this.themeLineItemTypes = themeLineItemTypes ?? [];
      });
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 2000 });
  }
}

export class ProductSelectorDialogData {
  templateNameFilter: string | undefined;
  companyId: string | undefined;
  groupKey: string | undefined;
  propertyName: string | undefined;
  relatedTagsOrder: string[] | undefined;
  tagGroupsFilter: string[] | undefined;
  tagSelectionEnabled: boolean | undefined; // Allow the user to select tags after selecting a product
  multiSelect: boolean | undefined;
}

export interface ProductSelectorResultData {
  products: Product2[];
  type: ThemeLineItemType | undefined;
  tags: string[];
  scope: RoomType | undefined;
  scopeVariant: ScopeVariant | undefined;
  groupKey: string | undefined;
  propertyName: string | undefined;
  tagSelectionEnabled: boolean | undefined;
}
